import React from 'react';
import { Link } from 'react-router-dom';
import './IndexLanding.css';

interface SignalLandingConfig {
  symbol: string;
  logoText: string;
  title: string;
  heroTitle: string;
  heroSubtitle: string;
  price: string;
  badge: string;
  color: string;
  statsAnalyzed: string;
  statsSnapshots: string;
  instrumentType: string;
  featureExtra: { icon: string; title: string; desc: string };
  ctaText: string;
  footerText: string;
}

export const SIGNAL_CONFIGS: Record<string, SignalLandingConfig> = {
  spy: {
    symbol: 'SPY',
    logoText: 'SPY',
    title: 'SPY Signals',
    heroTitle: 'Find the Best SPY Put to Sell. Every Day.',
    heroSubtitle: 'Our system analyzes hundreds of SPY equity put options across every expiration, scores them with a proprietary quantitative model, then validates the top pick with two independent AI engines — so you trade with confidence, not guesswork.',
    price: '$49',
    badge: 'AI-POWERED SPY OPTIONS INTELLIGENCE',
    color: '#06b6d4',
    statsAnalyzed: '300+',
    statsSnapshots: '3x',
    instrumentType: 'equity options',
    featureExtra: {
      icon: '\u{1F4B0}',
      title: 'Lower Capital Requirement',
      desc: 'SPY options trade at a fraction of SPX notional value, making put-selling accessible for accounts of all sizes. Same strategy, smaller position sizing.',
    },
    ctaText: 'Ready to Trade SPY Options Smarter?',
    footerText: 'SPY Signals is designed for educational and analytical purposes. All trading involves risk. Past research performance does not guarantee future results.',
  },
  es: {
    symbol: 'ES',
    logoText: 'ES',
    title: 'ES Signals',
    heroTitle: 'Find the Best E-mini Put to Sell. Every Day.',
    heroSubtitle: 'Our system analyzes hundreds of E-mini S&P 500 futures put options across every expiration, scores them with a proprietary quantitative model, then validates the top pick with two independent AI engines — built for serious futures traders.',
    price: '$79',
    badge: 'AI-POWERED FUTURES OPTIONS INTELLIGENCE',
    color: '#f59e0b',
    statsAnalyzed: '400+',
    statsSnapshots: '3x',
    instrumentType: 'futures options',
    featureExtra: {
      icon: '\u{1F30D}',
      title: 'Extended Trading Hours',
      desc: 'ES futures trade nearly 24 hours. Our system captures after-hours snapshots at 6 PM, 8 PM, and 10 PM ET — so you see opportunities that equity-only traders miss.',
    },
    ctaText: 'Ready to Trade E-mini Futures Options Smarter?',
    footerText: 'ES Signals is designed for educational and analytical purposes. Futures trading involves substantial risk. Past research performance does not guarantee future results.',
  },
  mes: {
    symbol: 'MES',
    logoText: 'MES',
    title: 'MES Signals',
    heroTitle: 'Micro E-mini Signals. Full-Size Intelligence.',
    heroSubtitle: 'The same quantitative analysis and AI validation as our ES research — applied to Micro E-mini S&P 500 futures options. Professional-grade research for smaller accounts.',
    price: '$49',
    badge: 'AI-POWERED MICRO FUTURES INTELLIGENCE',
    color: '#10b981',
    statsAnalyzed: '400+',
    statsSnapshots: '3x',
    instrumentType: 'micro futures options',
    featureExtra: {
      icon: '\u{1F4AA}',
      title: 'Perfect for Smaller Accounts',
      desc: 'MES contracts are 1/10th the size of ES. Get the same futures market exposure and research quality with significantly less capital at risk — ideal for building confidence.',
    },
    ctaText: 'Ready to Trade Micro Futures Options Smarter?',
    footerText: 'MES Signals is designed for educational and analytical purposes. Futures trading involves substantial risk. Past research performance does not guarantee future results.',
  },
};

interface Props {
  product: 'spy' | 'es' | 'mes';
}

export const SignalLanding: React.FC<Props> = ({ product }) => {
  const c = SIGNAL_CONFIGS[product];

  return (
    <div className="idxl-page">
      {/* ---- Nav ---- */}
      <nav className="idxl-nav">
        <Link to="/" className="idxl-nav-brand">
          <div className="idxl-nav-logo" style={{ backgroundColor: c.color }}>{c.logoText}</div>
          <span className="idxl-nav-title">{c.title}</span>
        </Link>
        <div className="idxl-nav-actions">
          <Link to="/" className="idxl-nav-link">Home</Link>
          <Link to="/login?redirect=upgrade" className="idxl-btn idxl-btn-primary" style={{ backgroundColor: c.color, borderColor: c.color }}>Get Started</Link>
        </div>
      </nav>

      {/* ---- Hero ---- */}
      <section className="idxl-hero">
        <div className="idxl-hero-badge" style={{ backgroundColor: `${c.color}20`, color: c.color, border: `1px solid ${c.color}40` }}>{c.badge}</div>
        <h1>{c.heroTitle}</h1>
        <p className="idxl-hero-subtitle">{c.heroSubtitle}</p>
        <div style={{ margin: '1.5rem 0', display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '3rem', fontWeight: 800, color: c.color }}>{c.price}</span>
          <span style={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.5)' }}>/month</span>
        </div>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>60-day money-back guarantee. Cancel anytime.</p>
        <div className="idxl-hero-actions">
          <Link to="/login?redirect=upgrade" className="idxl-btn idxl-btn-primary idxl-btn-lg" style={{ backgroundColor: c.color, borderColor: c.color }}>
            Subscribe Now
          </Link>
          <a href="#how-it-works" className="idxl-btn idxl-btn-outline idxl-btn-lg">
            See How It Works
          </a>
        </div>
      </section>

      {/* ---- Stats Strip ---- */}
      <section className="idxl-stats">
        <div className="idxl-stat">
          <div className="idxl-stat-val">{c.statsAnalyzed}</div>
          <div className="idxl-stat-label">Put Options Analyzed</div>
        </div>
        <div className="idxl-stat">
          <div className="idxl-stat-val">{c.statsSnapshots}</div>
          <div className="idxl-stat-label">Daily Snapshots</div>
        </div>
        <div className="idxl-stat">
          <div className="idxl-stat-val">3</div>
          <div className="idxl-stat-label">Independent Validations</div>
        </div>
        <div className="idxl-stat">
          <div className="idxl-stat-val">6</div>
          <div className="idxl-stat-label">Scoring Factors</div>
        </div>
      </section>

      {/* ---- How It Works ---- */}
      <section className="idxl-section" id="how-it-works">
        <div className="idxl-section-label">How It Works</div>
        <h2 className="idxl-section-title">From Raw Data to High-Confidence Trades</h2>
        <p className="idxl-section-desc">
          Three times every trading day, our system scans the entire {c.symbol} options market,
          scores hundreds of trades, and tells you which one looks best —
          all in under 15 minutes, completely automated.
        </p>

        <div className="idxl-pipeline">
          <div className="idxl-pipe-step">
            <div className="idxl-pipe-num" style={{ backgroundColor: c.color }}>1</div>
            <div className="idxl-pipe-title">We Grab Live Prices</div>
            <div className="idxl-pipe-desc">
              Every {c.symbol} put option's real-time price, risk metrics, and market
              conditions — pulled straight from the brokerage, not delayed or
              estimated.
            </div>
          </div>
          <div className="idxl-pipe-step">
            <div className="idxl-pipe-num" style={{ backgroundColor: c.color }}>2</div>
            <div className="idxl-pipe-title">We Score Every Trade</div>
            <div className="idxl-pipe-desc">
              Our model rates each option on: how much you earn, how safe it is,
              how likely you win, and how fast it pays. Trades are compared
              within the same time window so the ranking is fair.
            </div>
          </div>
          <div className="idxl-pipe-step">
            <div className="idxl-pipe-num" style={{ backgroundColor: c.color }}>3</div>
            <div className="idxl-pipe-title">Two AIs Double-Check</div>
            <div className="idxl-pipe-desc">
              The top picks go to Claude (Anthropic) and ChatGPT (OpenAI)
              for a second and third opinion. Neither AI sees our rankings —
              they judge independently.
            </div>
          </div>
          <div className="idxl-pipe-step">
            <div className="idxl-pipe-num" style={{ backgroundColor: c.color }}>4</div>
            <div className="idxl-pipe-title">You Get a Confidence Level</div>
            <div className="idxl-pipe-desc">
              When all three agree on the best trade — HIGH confidence.
              When they disagree — MEDIUM or LOW, so you know to look
              closer before acting.
            </div>
          </div>
        </div>
      </section>

      {/* ---- Features ---- */}
      <section className="idxl-section">
        <div className="idxl-section-label">What You Get</div>
        <h2 className="idxl-section-title">Everything You Need to Trade {c.symbol} Options</h2>
        <p className="idxl-section-desc">
          No spreadsheets. No guessing. You get clear recommendations with
          plain-English explanations of why each trade was picked — so you
          always know what you're looking at.
        </p>

        <div className="idxl-features">
          {[
            { icon: 'TOP 3', title: 'Three Best Picks', desc: 'See the #1 trade from our model, Claude, and ChatGPT side by side. Same format, same metrics — so you can compare instantly and pick with confidence.' },
            { icon: 'DTE', title: 'Grouped by Time Horizon', desc: 'Trades expiring this week compete against each other, not against monthly trades. Fair comparisons mean better picks, whether you want quick wins or longer setups.' },
            { icon: 'AI', title: 'AI Explains Its Thinking', desc: 'Not just a number — the AI tells you why it chose each trade. Weekend risk? Low volume? Unusual skew? You see the reasoning in plain English.' },
            { icon: c.featureExtra.icon === '\u{1F4B0}' ? '$' : c.featureExtra.icon === '\u{1F30D}' ? '24H' : c.featureExtra.icon === '\u{1F4AA}' ? '1/10' : '?', title: c.featureExtra.title, desc: c.featureExtra.desc },
            { icon: 'DOC', title: 'Download Full Reports', desc: 'Get a Word document with everything — rankings, AI picks, reasoning, and market context. Save it, print it, or share it with your trading group.' },
            { icon: 'HX', title: 'Check Past Picks', desc: 'Browse any past day to see what was recommended and how it played out. Full transparency — nothing is hidden or deleted.' },
          ].map((f) => (
            <div className="idxl-feat" key={f.title}>
              <div className="idxl-feat-icon" style={{ background: `${c.color}15`, color: c.color, borderRadius: '8px', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.02em', border: `1px solid ${c.color}30` }}>{f.icon}</div>
              <div className="idxl-feat-title">{f.title}</div>
              <div className="idxl-feat-desc">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- Tri-Validation ---- */}
      <section className="idxl-section">
        <div className="idxl-section-label">Triple Validation</div>
        <h2 className="idxl-section-title">Three Independent Minds. One Recommendation.</h2>
        <p className="idxl-section-desc">
          Instead of relying on one source, we use three. Our math model crunches the numbers.
          Two world-class AIs review the same data independently. When all three point
          to the same trade — that's a read worth acting on.
        </p>

        <div className="idxl-tri-grid">
          {[
            { cls: 'idxl-tri-card-model', label: 'QM', name: 'Our Quantitative Model', desc: 'Our math engine scores every option on how much you could earn, how safe the trade is, and how likely it is to win. It compares options fairly within the same time window so no trade gets an unfair advantage.', bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.3)', labelColor: '#f59e0b' },
            { cls: 'idxl-tri-card-claude', label: 'CL', name: 'Claude AI (Anthropic)', desc: 'Anthropic\'s advanced AI reads the same raw market data and picks its own best trade — completely independent from our model. It explains its reasoning so you can see exactly why it chose each pick.', bg: 'rgba(139,92,246,0.1)', border: 'rgba(139,92,246,0.3)', labelColor: '#a78bfa' },
            { cls: 'idxl-tri-card-gpt', label: 'GP', name: 'ChatGPT (OpenAI)', desc: 'OpenAI\'s leading AI provides a second independent opinion on the same data. When all three agree on the best trade, you can act with much higher confidence than trusting any single source alone.', bg: 'rgba(20,184,166,0.1)', border: 'rgba(20,184,166,0.3)', labelColor: '#2dd4bf' },
          ].map((t) => (
            <div className={`idxl-tri-card ${t.cls}`} key={t.name}>
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: t.bg, border: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.75rem', fontSize: '0.85rem', fontWeight: 800, color: t.labelColor }}>{t.label}</div>
              <div className="idxl-tri-name">{t.name}</div>
              <div className="idxl-tri-desc">{t.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- Why Not ChatGPT ---- */}
      <section className="idxl-section" style={{ background: `${c.color}05`, borderTop: `1px solid ${c.color}15`, borderBottom: `1px solid ${c.color}15` }}>
        <div className="idxl-section-label">Why Us, Not ChatGPT</div>
        <h2 className="idxl-section-title">A Quantitative Engine, Not a Chatbot</h2>
        <p className="idxl-section-desc">
          ChatGPT cannot see live option chains, evaluate real bid/ask spreads, or manage trading risk.
          Our system does all of this automatically, every snapshot.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem', maxWidth: '800px', margin: '0 auto' }}>
          {[
            { label: 'Live Market Data', us: 'Real bid/ask from TastyTrade WebSocket', them: 'No live data access' },
            { label: 'Option Chain Analysis', us: `${c.statsAnalyzed} puts scored per snapshot`, them: 'Cannot read option chains' },
            { label: 'Risk Management', us: 'Min credit, safety cushion, DTE weighting', them: 'No risk parameters' },
            { label: 'Transparency', us: 'Downloadable reports with full rationale', them: 'Text response, no audit trail' },
          ].map((row) => (
            <div key={row.label} style={{ padding: '1.25rem', borderRadius: '10px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.75rem', color: '#e2e8f0' }}>{row.label}</div>
              <div style={{ fontSize: '0.8rem', color: c.color, marginBottom: '0.5rem', lineHeight: 1.5 }}>
                <span style={{ fontWeight: 600 }}>Us:</span> {row.us}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.5 }}>
                <span style={{ fontWeight: 600 }}>ChatGPT:</span> {row.them}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- Pricing ---- */}
      <section className="idxl-section">
        <div className="idxl-section-label">Pricing</div>
        <h2 className="idxl-section-title">{c.symbol} Signals — {c.price}/month</h2>
        <p className="idxl-section-desc">
          Professional-grade quantitative research powered by live market data.
          60-day money-back guarantee. Cancel anytime.
        </p>
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <Link to="/login?redirect=upgrade" className="idxl-btn idxl-btn-primary idxl-btn-lg" style={{ backgroundColor: c.color, borderColor: c.color }}>
            Subscribe to {c.symbol} Signals
          </Link>
          <p style={{ marginTop: '1rem', color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>
            Want everything? Get the <Link to="/login?redirect=upgrade" style={{ color: '#ec4899' }}>All Signals Bundle</Link> for $199/mo and save $57/mo.
          </p>
        </div>
      </section>

      {/* ---- CTA ---- */}
      <section className="idxl-cta">
        <h2>{c.ctaText}</h2>
        <p>
          Join traders who rely on triple-validated research — not gut feelings —
          to sell {c.symbol} puts with confidence.
        </p>
        <div className="idxl-hero-actions">
          <Link to="/login?redirect=upgrade" className="idxl-btn idxl-btn-primary idxl-btn-lg" style={{ backgroundColor: c.color, borderColor: c.color }}>
            Subscribe Now
          </Link>
          <Link to="/login" className="idxl-btn idxl-btn-outline idxl-btn-lg">
            Sign In
          </Link>
        </div>
      </section>

      {/* ---- Footer ---- */}
      <footer className="idxl-footer">
        {c.footerText}
      </footer>
    </div>
  );
};
